import { InputLabel, LinearProgress, MenuItem, Select } from '@mui/material';
import { Content, PageOrientation, PageSize, TDocumentDefinitions } from 'pdfmake/interfaces';
import { FC, useCallback, useState } from 'react';
import OrganizationSurvey from '../../api/organizationSurvey';
import Survey from '../../api/survey';
import { pdfService } from '../../services/pdfService';
import ButtonComponent from '../FormComponents/Button';
import Modal from '../Modal/Modal';
import styles from './ExportModal.module.scss';

interface ConfirmationProps {
  showExportPopup: boolean;
  setShowExportPopup: (newValue: boolean) => void;
  organizationId: string;
  surveyId: string;
}

export const ExportModal: FC<ConfirmationProps> = ({
  showExportPopup,
  setShowExportPopup,
  organizationId,
  surveyId,
}) => {
  const [pageSize, setPageSize] = useState<PageSize>('A4');
  const [pageOrientation, setPageOrientation] = useState<PageOrientation>('portrait');
  const [loading, setLoading] = useState(false);

  const exportAsPdf = useCallback(
    async (
      cb: () => Promise<{
        fileName: string;
        content: Content;
        images: TDocumentDefinitions['images'];
      }>,
    ) => {
      setLoading(true);
      const dd = await cb();
      pdfService.triggerDownload(dd, pageSize, pageOrientation);
      setLoading(false);
      setShowExportPopup(false);
    },
    [pageOrientation, pageSize, setShowExportPopup],
  );

  const exportEmptySurveyAsPDF = useCallback(
    async () =>
      exportAsPdf(async () => {
        const survey = await Survey.getSurvey(surveyId);
        const dd = pdfService.constructContent({ survey });
        return dd;
      }),
    [exportAsPdf, surveyId],
  );

  const exportFilledSurveyAsPDF = useCallback(
    async () =>
      exportAsPdf(async () => {
        const data = await OrganizationSurvey.getOrganizationSurvey({ id: surveyId, organizationId });
        const dd = pdfService.constructContent({ ...data });
        return dd;
      }),
    [exportAsPdf, organizationId, surveyId],
  );

  return (
    <Modal
      open={showExportPopup}
      onClose={() => {
        setShowExportPopup(false);
      }}
      title='Export as PDF'
      data={
        <div className={styles.container}>
          <div className={styles.paramsContainer}>
            <InputLabel variant='standard' className={styles.paramsItem}>
              Page Size
              <Select
                value={pageSize}
                onChange={e => setPageSize(e.target.value as PageSize)}
                size='small'
                variant='standard'
              >
                <MenuItem value='A4'>A4</MenuItem>
                <MenuItem value='Letter'>Letter</MenuItem>
              </Select>
            </InputLabel>

            <InputLabel variant='standard' className={styles.paramsItem}>
              Page Orientation
              <Select
                value={pageOrientation}
                onChange={e => setPageOrientation(e.target.value as PageOrientation)}
                size='small'
                variant='standard'
              >
                <MenuItem value='portrait'>Portrait</MenuItem>
                <MenuItem value='landscape'>Landscape</MenuItem>
              </Select>
            </InputLabel>
          </div>

          <LinearProgress style={{ visibility: loading ? 'visible' : 'hidden' }} />
          <ButtonComponent text='Export empty version' onClick={exportEmptySurveyAsPDF} variant='outlined' />
          {!!organizationId && <ButtonComponent text='Export last saved version' onClick={exportFilledSurveyAsPDF} />}
        </div>
      }
    />
  );
};
